import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AUTH_HTTP_INTERCEPTOR_PROVIDERS } from './auth/auth.module';
import { GraphQLModule } from './graphql.module';
import { AnonymousReportInterceptor } from './incident/anonymous-report.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { WINDOW_PROVIDERS } from 'src/util/window.provider';
import { NgAudioRecorderModule } from 'ng-audio-recorder';
import { AssetsCacheBusterInterceptor } from './shared/interceptors/assets-cache-buster.interceptor';
import { SharedModule } from './shared/shared.module';
import { LOCATION_INITIALIZED } from '@angular/common';
import { take } from 'rxjs/operators'


export function createTranslateLoader(http: HttpClient):TranslateHttpLoader  {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Combine plugin interceptors with app specific interceptors
export const APP_HTTP_INTERCEPTOR_PROVIDERS = [
  ...AUTH_HTTP_INTERCEPTOR_PROVIDERS,
  { provide: HTTP_INTERCEPTORS, useClass: AssetsCacheBusterInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: AnonymousReportInterceptor, multi: true }
];

/**  
 * @see: https://github.com/ngx-translate/core/issues/517#issuecomment-299637956
 * We need to preload translations using .use() method. The problem is the method is async (we should wait for the result). 
 * The best way to force the application to wait for "something" to finish before it shows up, is using APP_INITIALIZER function.
 * Now application will wait for translations initialization before it shows up for user.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function appInitializerFactory(translateService: TranslateService, injector: Injector): () => Promise<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return () => new Promise<any>((resolve: any) => {
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    locationInitialized.then(() => {
      const langToSet = 'en';
      translateService.setDefaultLang('en');
      translateService.use(langToSet).pipe(take(1)).subscribe(() => {
        //console.info(`Successfully initialized '${langToSet}' language.'`);
      }, err => {
        console.error(err), () => resolve(null);
        console.error(`Problem with '${langToSet}' language initialization.'`);
      }, () => {
        resolve(null);
      });
    });
  });
}

@NgModule({
  declarations: [AppComponent],
  entryComponents: [],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    ReactiveFormsModule,
    HttpClientModule,
    NgAudioRecorderModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
      isolate: false,
    }),
    GraphQLModule,
    SharedModule
  ],
  exports: [],
  providers: [
    APP_HTTP_INTERCEPTOR_PROVIDERS,
    WINDOW_PROVIDERS,
    StatusBar,
    SplashScreen,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true
  }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
